import type { JSX } from 'react';
import { Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { api, projectDataApi, type ProjectDetails } from '../api';
import { formatDate } from '../format';

/**
 * A project's description and dates from its master data (module 2), for the
 * project tiles and the header. Shows nothing when the module is off or the
 * project has no master data - the core must not depend on it.
 */
function useDetails(projectId: string): ProjectDetails | null {
  const modules = useQuery({ queryKey: ['modules'], queryFn: api.modules, staleTime: 60_000 });
  const active = modules.data?.some((m) => m.key === 'projectdata' && m.active) ?? false;
  const data = useQuery({
    queryKey: ['projectdata', projectId],
    queryFn: () => projectDataApi.get(projectId),
    enabled: active,
    staleTime: 60_000,
    retry: false,
  });
  return data.data?.details ?? null;
}

/** "Angebot 21.10.2026 · Bindefrist 20.11.2026 · Frist 31.12.2026" - only the dates that are set. */
export function projectDates(d: ProjectDetails): string {
  return [
    d.offerDate && `Angebot ${formatDate(d.offerDate)}`,
    d.bindingUntil && `Bindefrist ${formatDate(d.bindingUntil)}`,
    d.hardDeadline && `Frist ${formatDate(d.hardDeadline)}`,
  ].filter(Boolean).join(' · ');
}

export function ProjectSummary({ projectId, lines = 2 }: { projectId: string; lines?: number }): JSX.Element | null {
  const d = useDetails(projectId);
  if (!d) return null;
  const dates = projectDates(d);
  if (!d.description && !dates && !d.client) return null;
  return (
    <>
      {d.description && <Text size="xs" c="dimmed" lineClamp={lines} title={d.description}>{d.description}</Text>}
      {(dates || d.client) && (
        <Text size="xs" c="dimmed" mt={2} lineClamp={1}>
          {[d.client?.split(',')[0], dates].filter(Boolean).join(' · ')}
        </Text>
      )}
    </>
  );
}
